<template>
	<div class="edit-row edit-row--b-top black">
		<div class="field-item mod--edit-bar">
			<div
				@click="optionSet(TEMPLATE_OPTION)"
				:class="isBarTemplateActiveClassname"
				class="select field--w-175"
			>
				<div class="select__header">
					<div :class="templateMarkedClassname" class="select__value">{{ template }}</div>
					<div class="select__icon">
						<i class="fas fa-chevron-down"></i>
					</div>
				</div>
			</div>
		</div>
		<div class="quick-control flex--justify-between">
			<div
				v-for="option in EDGE_EDIT_BAR_OPTIONS"
				:key="option.VALUE"
				@click="optionSet(option)"
				class="quick-control__item"
				:class="{
					active: optionActive === option.VALUE,
					blocked:
						isRestricted(option.TYPE) ||
						edgeEditing && option.VALUE === EDGE_EDIT_BAR_OPTIONS.DESIGN_SETTINGS.VALUE
				}"
			>
				<i :class="option.ICON"></i>
				<div
					v-if="edgeEditing && option.TYPE === EDGE_EDIT_BAR_OPTIONS.DESIGN_SETTINGS.VALUE"
					class="quick-control__map-user map-user map-user--small"
					:class="edgeEditing.colorName"
				>{{ edgeEditing.initials }}</div>
			</div>
		</div>
	</div>
	<popup-confirm
		:popup-status="popupActive"
		:content="popupMessage"
		@action-confirm="edgeRemove"
		@action-cancel="popupClose"
		:loading="loading"
	>
    <field-checkbox
        class="mt--15"
        :name="checkboxTitle"
        :use-change-emit="true"
        :checked="!removalConfirmation"
        @changeChecked="removalConfirmationChange"
    ></field-checkbox>
  </popup-confirm>
</template>

<script>
import PopupConfirm from '@/components/popup/PopupConfirm';
import {useI18n} from 'vue-i18n';
import {useStore} from 'vuex';
import usePopup from '@/utils/Popup';
import {computed, inject, onMounted, onUnmounted, ref} from 'vue';
import PermissionService from '@/utils/PermissionService';
import {Roles} from '@/models/Roles';
import {DASHBOARD_OPTIONS} from '@/models/DASHBOARD_OPTIONS';
import {EDGE_EDIT_BAR_OPTIONS} from '@/models/edit-bar/EDGE_EDIT_BAR_OPTIONS';
import {TEMPLATE_VALUE} from "@/models/TEMPLATE_VALUE";
import {EDITING_EXTENT_TYPES} from '@/models/EDITING_EXTENT_TYPES';
import useLoading from '@/utils/Loading';
import FieldCheckbox from "@/components/fields/FieldCheckbox.vue";
export default {
	components: {FieldCheckbox, PopupConfirm},
	emits: ['optionSet'],
	setup(props, {emit}) {
		const { t } = useI18n();
		const store = useStore();
		const emitter = inject('emitter');
		const {loading, loadingShow, loadingHide} = useLoading();
		// popup
		const { popupActive, popupOpen, popupClose } = usePopup();
		
		const currentEdge = computed(() => store.getters['edges/getSelectedEdge']);
		const edgeEditing = computed(() => currentEdge.value.editing);
		const editedEdge = computed(() => store.getters['simultaneousEditing/getEditedEdge']);
		const EDGE_SETTINGS = DASHBOARD_OPTIONS.CREATE.GROUPS.EDGE.EDGE_DESIGN;
		async function optionChoose(option){
			await store.dispatch('dashboardOptions/optionChoose', option);
		}
		
		// template
		const templates = computed(() => store.getters["design/getEdgesTemplates"]);
		const template = computed(() => currentEdge.value.template === '0' ? TEMPLATE_VALUE.GLOBAL.VALUE : templates.value[currentEdge.value.template]?.name || TEMPLATE_VALUE.NONE.VALUE)
		const isTemplateChangesTracked = computed(() => store.getters["edges/isTemplateChangesTracked"]);
		const templateMarkedClassname = computed(() => isTemplateChangesTracked.value ? 'marked' : '');
		const TEMPLATE_OPTION = {VALUE: 'TEMPLATE', TYPE: 'TEMPLATE', ICON: null, ACTION: null};
		const isBarTemplateActiveClassname = computed(() => optionActive.value === TEMPLATE_OPTION.VALUE ? 'selected--edit-bar' : '');
		
		const userRole = computed(() => store.getters["user/getCurrentUserRole"]);
		const hasUserPermission = computed(() => PermissionService.permissionCheck({userRole: userRole.value, permission: Roles.Student}))
		const hasUserPermissionClassname = computed(() => hasUserPermission.value ? "" : "blocked");
		
		const clientRestrictions = computed(() => store.getters["clients/getRestrictions"]);
		const hasCustomDesign = computed(() => clientRestrictions.value.hasCustomDesign.value);
		function isRestricted(link){
			if(!clientRestrictions.value) return false;
			for(const restriction of Object.values(clientRestrictions.value)){
				if(restriction.option && restriction.option === link && !restriction.value) {
					return true;
				}
			}
		}

    // EDGE REMOVE
    const popupMessage = computed(() => `${t('popup.confirm.warning', {action: t('popup.confirm.actions.deleteEdge')})}`)
    const checkboxTitle = computed(() => t('popup.confirm.showMessage'));
    const removalConfirmation = computed(() => store.getters["user/getRemovalConfirmation"])
    async function removalConfirmationChange(value){
      await store.dispatch('user/userPreferencesSet', { removalConfirmation: !value})
    }
    function checkRemovalConfirmation(){
      if(removalConfirmation.value) edgeRemovePopupOpen();
      if(!removalConfirmation.value) edgeRemove();
    }
		function edgeRemovePopupOpen(){
			if(!hasUserPermission.value) return;
			popupOpen();
		}
		async function edgeRemove(){
			const edgeId = currentEdge.value.id;
			loadingShow();
			await store.dispatch('edges/remove/edgeRemoveRequest', edgeId)
			loadingHide();
			popupClose();
		}
		
		const optionDashboardActive = computed(() => store.getters["dashboardOptions/getOptionActive"]);
		const isEdgeDesignSettingsOpened = computed(() => optionDashboardActive.value?.VALUE === EDGE_SETTINGS.VALUE);
		async function toggleDesignSettings(){
			isEdgeDesignSettingsOpened.value ? await optionChoose(null) : await optionChoose(EDGE_SETTINGS);
		}
		
		const optionActive = ref(null);
		function optionActiveToggle(option){
			optionActive.value = optionActive.value === option ? null : option;
		}
		function optionActiveClose() {
			optionActive.value = null;
		}
		function optionEmit(option){
			if(option) emit('optionSet', option);
			if(!option) emit('optionSet', null);
		}
		async function optionSet(option){
			if(isRestricted(option.TYPE)) return;
			if(option.TYPE === EDGE_EDIT_BAR_OPTIONS.DESIGN_SETTINGS.VALUE && edgeEditing.value) return;
			if(option.VALUE === EDGE_EDIT_BAR_OPTIONS.CONTENT.VALUE && edgeEditing.value) return;
			if(option.ACTION){
				switch (option.ACTION){
					/*case EDGE_EDIT_BAR_OPTIONS.DESIGN_SETTINGS.ACTION: {
						optionEmit(null);
						toggleDesignSettings();
						break;
					}*/
					case EDGE_EDIT_BAR_OPTIONS.DELETE.ACTION: {
            checkRemovalConfirmation();
						break;
					}
				}
			} else {
				switch(option.VALUE){
					case EDGE_EDIT_BAR_OPTIONS.DESIGN_SETTINGS.VALUE: {
						const option = EDGE_EDIT_BAR_OPTIONS.DESIGN_SETTINGS.VALUE;
						optionActiveToggle(option);
						optionEmit(option);
						
						if(editedEdge.value && !isEdgeDesignSettingsOpened.value){
							await store.dispatch('simultaneousEditing/stopEditingEdge', null);
						} else {
							const edgeData = {
								edgeId: currentEdge.value.id,
								lockExtent: EDITING_EXTENT_TYPES.APPEARANCE
							}
							await store.dispatch('simultaneousEditing/startEditingEdge', edgeData);
						}
						if(isEdgeDesignSettingsOpened.value){
							await store.dispatch('settingsSidebar/sidebarClose', null, { root: true});
							await store.dispatch('dashboardOptions/optionDrop');
						}
						break;
					}
					case EDGE_EDIT_BAR_OPTIONS.VISIBILITY.VALUE: {
						const option = EDGE_EDIT_BAR_OPTIONS.VISIBILITY.VALUE;
						optionActiveToggle(option);
						optionEmit(option);
						checkEdited();
						break;
					}
					case EDGE_EDIT_BAR_OPTIONS.CONTENT.VALUE: {
						const option = EDGE_EDIT_BAR_OPTIONS.CONTENT.VALUE;
						optionActiveToggle(option);
						optionEmit(option);
						checkEdited();
						if(isEdgeDesignSettingsOpened.value){
							await store.dispatch('settingsSidebar/sidebarClose', null, { root: true});
							await store.dispatch('dashboardOptions/optionDrop');
						}
						break;
					}
					case TEMPLATE_OPTION.VALUE: {
						const option = TEMPLATE_OPTION.VALUE;
						optionActiveToggle(option);
						optionEmit(option);
						break;
					}
				}
			}
		}
		
		function checkEdited(){
			if(editedEdge.value && !isEdgeDesignSettingsOpened.value) store.dispatch('simultaneousEditing/stopEditingEdge', null);
		}
		
		function closePanel(){
			optionActiveClose();
			optionEmit();
		}
		
		onMounted(() => {
			emitter.on('closeQuickEditBarPanel', closePanel);
		})
		onUnmounted(() => {
			emitter.off('closeQuickEditBarPanel', closePanel);
		})
	
		return {
			EDGE_EDIT_BAR_OPTIONS,
			loading,
			optionActive,
			popupActive,
			popupClose,
			popupMessage,
			edgeRemove,
			optionSet,
			template,
			templateMarkedClassname,
			TEMPLATE_OPTION,
			isBarTemplateActiveClassname,
			isRestricted,
			edgeEditing,
      removalConfirmation,
      removalConfirmationChange,
      checkboxTitle
		}
	}
}
</script>

<style lang="scss">

</style>
